import { useEffect, useMemo } from "react";
import { createSearchParams, type SetURLSearchParams } from "react-router";

import { usePersistentState } from "~/hooks/usePersistentState";
import {
  extractPersistableLibraryViewParams,
  getLibraryViewPersistenceEnabledStorageKey,
  getLibraryViewStorageKey,
  hasExplicitLibraryViewState,
  readPersistedLibraryViewParams,
  writePersistedLibraryViewParams,
} from "~/helpers/libraryViewPersistence";

interface UseLibraryViewPersistenceParams {
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
  userId: number;
}

/**
 * Keeps the library's filter and sort params in localStorage per user, when
 * the user has opted in (off by default).
 *
 * - Restore: whenever the URL states no library view at all (no filters, sort
 *   or title search), the stored params are applied and the URL is replaced.
 * - Persist: `setLibraryViewParams` writes the filter/sort subset of the new
 *   params before navigating. Title-search changes should use the plain
 *   `setSearchParams` so they never touch storage.
 * - Toggle: enabling saves the current view right away; disabling removes the
 *   saved view.
 */
export const useLibraryViewPersistence = ({
  searchParams,
  setSearchParams,
  userId,
}: UseLibraryViewPersistenceParams) => {
  const storageKey = getLibraryViewStorageKey(userId);
  const [isEnabled, setIsEnabledState] = usePersistentState(
    getLibraryViewPersistenceEnabledStorageKey(userId),
    false,
  );

  const restoredParams = useMemo(() => {
    if (!isEnabled || hasExplicitLibraryViewState(searchParams)) {
      return undefined;
    }
    const persistedParams = readPersistedLibraryViewParams(storageKey);
    return persistedParams
      ? new URLSearchParams([...searchParams, ...persistedParams])
      : undefined;
  }, [isEnabled, searchParams, storageKey]);

  useEffect(() => {
    if (restoredParams) {
      setSearchParams(restoredParams, { replace: true });
    }
  }, [restoredParams, setSearchParams]);

  const setLibraryViewParams: SetURLSearchParams = (
    nextInit,
    navigateOptions,
  ) => {
    const next = createSearchParams(
      typeof nextInit === "function" ? nextInit(searchParams) : nextInit,
    );
    if (isEnabled) {
      writePersistedLibraryViewParams(
        storageKey,
        extractPersistableLibraryViewParams(next),
      );
    }
    setSearchParams(next, navigateOptions);
  };

  const setIsEnabled = (enabled: boolean) => {
    writePersistedLibraryViewParams(
      storageKey,
      enabled
        ? extractPersistableLibraryViewParams(searchParams)
        : new URLSearchParams(),
    );
    setIsEnabledState(enabled);
  };

  // The restored params drive rendering until the URL replacement lands.
  return {
    searchParams: restoredParams ?? searchParams,
    setLibraryViewParams,
    isPersistenceEnabled: isEnabled,
    setIsPersistenceEnabled: setIsEnabled,
  };
};
